import { useState, useRef, useEffect } from "react";
import { Modal } from ".";
import Alert from "../alert/Alert";
import Toast from "../toast/Toast";
import { X, Lock } from "lucide-react";
import { autoClearError } from "../../../utils/helpers";

interface CreatePinModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Should call your createPin backend mutation/request and return true on success */
  onPinCreated: (pin: string) => Promise<boolean>;
  title?: string;
}

type Step = "create" | "confirm";

const emptyPin = ["", "", "", ""];

const CreatePinModalNew = ({
  isOpen,
  onClose,
  onPinCreated,
  title = "Create PIN",
}: CreatePinModalProps) => {
  const [step, setStep] = useState<Step>("create");
  const [pin, setPin] = useState<string[]>(emptyPin);
  const [confirmPin, setConfirmPin] = useState<string[]>(emptyPin);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Reset everything whenever the modal opens
  useEffect(() => {
    if (isOpen) {
      setStep("create");
      setPin(emptyPin);
      setConfirmPin(emptyPin);
      setError("");
      setSuccess("");
      focusFirstInput();
    }
  }, [isOpen]);

  const focusFirstInput = () => {
    setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 100);
  };

  const activeValues = step === "create" ? pin : confirmPin;
  const setActiveValues = step === "create" ? setPin : setConfirmPin;

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const updated = [...activeValues];
    updated[index] = value;
    setActiveValues(updated);

    if (value && index < 3) {
      setTimeout(() => {
        inputRefs.current[index + 1]?.focus();
      }, 10);
    }

    if (updated.every((digit) => digit !== "") && index === 3) {
      if (step === "create") {
        // Move on to the confirm step
        setTimeout(() => {
          setConfirmPin(emptyPin);
          setStep("confirm");
          setError("");
          focusFirstInput();
        }, 150);
      } else {
        void handleConfirm(updated.join(""));
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (!activeValues[index] && index > 0) {
        setTimeout(() => {
          inputRefs.current[index - 1]?.focus();
        }, 10);
      } else if (activeValues[index]) {
        const updated = [...activeValues];
        updated[index] = "";
        setActiveValues(updated);
      }
    }
  };

  const handleConfirm = async (confirmedPin: string) => {
    const originalPin = pin.join("");

    // Frontend check happens before anything is sent to the backend
    if (confirmedPin !== originalPin) {
      setError("PINs do not match. Please try again.");
      autoClearError(setError);
      setConfirmPin(emptyPin);
      focusFirstInput();
      return;
    }

    setLoading(true);
    setError("");

    try {
      const ok = await onPinCreated(originalPin);
      if (ok) {
        // Keep loading/disabled state on so inputs don't become interactive
        // again while the success toast is showing, then close.
        setSuccess("PIN created successfully!");
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        setError("Could not create PIN. Please try again.");
        autoClearError(setError);
        resetToStart();
        setLoading(false);
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
      autoClearError(setError);
      resetToStart();
      setLoading(false);
    }
  };

  const resetToStart = () => {
    setStep("create");
    setPin(emptyPin);
    setConfirmPin(emptyPin);
    focusFirstInput();
  };

  const handleClose = () => {
    setStep("create");
    setPin(emptyPin);
    setConfirmPin(emptyPin);
    setError("");
    setSuccess("");
    setLoading(false);
    onClose();
  };

  const resetPin = () => {
    setActiveValues(emptyPin);
    focusFirstInput();
  };

  const isCreateStep = step === "create";
  const description = isCreateStep
    ? "Choose a 4-digit PIN to secure your account"
    : "Re-enter your PIN to confirm";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className="max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-800 dark:bg-gray-900"
    >
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20">
              <Lock className="h-5 w-5 text-primary dark:text-white/90" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                {isCreateStep ? title : "Confirm PIN"}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {description}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
            type="button"
          >
            <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {success && (
        <div className="mb-6">
          <Toast variant="success" title={success} message="You can now use it to authorize transactions" />
        </div>
      )}

      {error && (
        <div className="mb-6">
          <Alert variant="error" title="Error" message={error} />
        </div>
      )}

      {/* PIN Inputs */}
      <div className="mb-8">
        <div className="mb-4 flex justify-center gap-3">
          {[0, 1, 2, 3].map((index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="password"
              maxLength={1}
              value={activeValues[index]}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="h-14 w-14 rounded-lg border-2 border-gray-200 bg-gray-50 text-center text-2xl font-bold tracking-widest text-gray-800 transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              disabled={loading || !!success}
              inputMode="numeric"
              autoComplete="one-time-code"
            />
          ))}
        </div>

        <div className="text-center text-sm text-gray-500 dark:text-gray-400">
          {isCreateStep
            ? "Enter your 4-digit security PIN"
            : "Enter the same PIN again to confirm"}
        </div>
      </div>

      {/* Actions */}
      <div className="space-y-3">
        <button
          onClick={() =>
            isCreateStep ? undefined : void handleConfirm(confirmPin.join(""))
          }
          disabled={loading || !!success || activeValues.join("").length !== 4}
          className="w-full rounded-lg bg-primary px-4 py-3 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {success ? "PIN created!" : loading ? "Creating..." : isCreateStep ? "Continue" : "Confirm PIN"}
        </button>

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={resetPin}
            className="text-sm text-gray-800 hover:text-gray-500 dark:text-white/90 dark:hover:text-gray-300"
            disabled={loading || !!success}
          >
            Clear PIN
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CreatePinModalNew;